import React, { useState, useRef } from "react";
import Dialog from "../Dialog";
import Button from "../Button";
import { Icon } from "@iconify/react";
import { Typography } from "../Typography";

const UploadFileDialog = ({ 
  isOpen, 
  onClose, 
  onUpload,
  ideaId 
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileTags, setFileTags] = useState({});
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const handleClose = () => {
    setSelectedFiles([]);
    setFileTags({});
    setError("");
    setUploading(false);
    onClose();
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newFiles = files.map((file) => ({
      file,
      id: Date.now() + Math.random(),
      name: file.name,
      size: formatFileSize(file.size),
      type: getFileExtension(file.name),
    }));

    setSelectedFiles((prev) => [...prev, ...newFiles]);
    setError("");
  };

  const handleRemoveFile = (fileId) => {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== fileId));
    setFileTags((prev) => {
      const newTags = { ...prev };
      delete newTags[fileId];
      return newTags;
    });
  };

  const handleTagChange = (fileId, tag) => {
    setFileTags((prev) => ({
      ...prev,
      [fileId]: tag,
    }));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError("Please select at least one file to upload.");
      return;
    }

    setUploading(true);
    setError("");

    try {
      const filesToUpload = selectedFiles.map((fileData) => ({
        file: fileData.file,
        tag: fileTags[fileData.id] || "",
      }));

      if (onUpload) {
        await onUpload(filesToUpload, ideaId);
      }
      
      handleClose();
    } catch (err) {
      setError(err.message || "Failed to upload files. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const getFileExtension = (filename) => {
    return filename.split(".").pop().toLowerCase();
  };

  const getFileIcon = (type) => {
    const icons = {
      pdf: { bg: "bg-red-500", text: "PDF" },
      jpg: { bg: "bg-purple-500", text: "JPG" },
      jpeg: { bg: "bg-purple-500", text: "JPG" },
      png: { bg: "bg-purple-500", text: "PNG" },
      mp4: { bg: "bg-blue-500", text: "MP4" },
      doc: { bg: "bg-blue-500", text: "DOC" },
      docx: { bg: "bg-blue-500", text: "DOC" },
      xlsx: { bg: "bg-green-500", text: "XLSX" },
      pptx: { bg: "bg-orange-500", text: "PPTX" },
      default: { bg: "bg-gray-500", text: "FILE" },
    };
    return icons[type?.toLowerCase()] || icons.default;
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Upload Files"
      description="Select files to upload for this idea. You can add a tag to categorize each file."
      maxWidth="max-w-2xl"
    >
      <div className="flex flex-col gap-4">
        {/* File Input Area */}
        <div className="border-2 border-dashed border-purple-300 rounded-lg p-6 bg-purple-50 hover:bg-purple-100 transition-colors">
          <label className="flex flex-col items-center justify-center cursor-pointer">
            <div className="flex flex-col items-center justify-center">
              <Icon
                icon="lucide:upload"
                className="h-10 w-10 text-purple-600 mb-3"
              />
              <Typography
                as="p"
                className="text-sm font-semibold text-slate-700 mb-1"
              >
                Click to upload or drag and drop
              </Typography>
              <Typography
                as="p"
                className="text-xs text-tertiary-600"
              >
                PDF, DOC, DOCX, JPG, PNG, MP4, XLSX, PPTX (max. 50MB per file)
              </Typography>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.mp4,.xlsx,.pptx"
              onChange={handleFileSelect}
            />
          </label>
        </div>

        {/* Selected Files List */}
        {selectedFiles.length > 0 && (
          <div className="flex flex-col gap-3 max-h-64 overflow-y-auto">
            {selectedFiles.map((fileData) => {
              const fileIcon = getFileIcon(fileData.type);
              return (
                <div
                  key={fileData.id}
                  className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg bg-white"
                >
                  {/* File Icon */}
                  <div
                    className={`w-10 h-10 ${fileIcon.bg} rounded flex items-center justify-center flex-shrink-0`}
                  >
                    <Typography
                      as="span"
                      className="text-xs font-semibold text-white"
                    >
                      {fileIcon.text}
                    </Typography>
                  </div>

                  {/* File Info */}
                  <div className="flex-1 flex flex-col gap-1 min-w-0">
                    <Typography
                      as="p"
                      className="text-sm text-primary-900 font-medium truncate"
                    >
                      {fileData.name}
                    </Typography>
                    <Typography
                      as="p"
                      className="text-xs text-tertiary-500"
                    >
                      {fileData.size}
                    </Typography>
                  </div>

                  {/* File Tag Input */}
                  <div className="flex-1 min-w-0">
                    <input
                      type="text"
                      placeholder="File tag (optional)"
                      value={fileTags[fileData.id] || ""}
                      onChange={(e) => handleTagChange(fileData.id, e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => handleRemoveFile(fileData.id)}
                    className="text-red-500 hover:text-red-700 transition-colors p-1"
                  >
                    <Icon icon="lucide:trash-2" className="h-5 w-5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <Typography as="p" className="text-sm text-red-600">
              {error}
            </Typography>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
          <Button
            variant="secondary"
            size="medium"
            onClick={handleClose}
            disabled={uploading}
            className="bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={handleUpload}
            disabled={uploading || selectedFiles.length === 0}
            className="bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <>
                <Icon icon="lucide:loader-2" className="h-4 w-4 animate-spin mr-2" />
                Uploading...
              </>
            ) : (
              <>
                <Icon icon="lucide:upload" className="h-4 w-4 mr-2" />
                Upload Files
              </>
            )}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default UploadFileDialog;

